import React, { useState } from "react";
import { graphql, useLazyLoadQuery } from "react-relay";
import {
  sortFloats,
  sortGolfLeaderboardPositions,
  sortGolfToPars,
  updateLocalMinMaxVal,
} from "../../../../../../Utils";
import LiveTournamentToolTable from "../LiveTournamentToolTable";
import StatTable from "../../../../../StatTable";
import DraftkingsInfoOverlay from "../../../../../DraftKingsInfoOverlay";

const STROKES_GAINED = graphql`
  query LiveStrokesGainedOverviewQuery($eventId: ID!) {
    liveStrokesGainedOverview(eventId: $eventId) {
      eventPlayers {
        player {
          id
          firstName
          lastName
          nameDisplay
          dkPlayer {
            id
          }
        }

        nextRoundSalary
        prettyEventToPar

        strokesGainedApproachTheGreen
        strokesGainedAroundTheGreen
        strokesGainedBallStriking
        strokesGainedIronPlay
        strokesGainedOffTheTee
        strokesGainedPutting
        strokesGainedShortGame
        strokesGainedTeeToGreen
        strokesGainedTotal

        currentPosition
      }

      nextRoundSalaryTitle
    }
  }
`;

export default function LiveStrokesGainedOverview({
  selectedEventID,
  strokesGainedType,
  roundNumber,
  myPlayers,
}) {
  const [minMaxVals, setMinMaxVals] = useState({});
  const [selectedDkPlayerId, setSelectedDkPlayerId] = useState(undefined);

  const queryData = useLazyLoadQuery(STROKES_GAINED, {
    eventId: selectedEventID,
  });

  const nextRoundSalaryTitle =
    queryData.liveStrokesGainedOverview.nextRoundSalaryTitle;

  const data = React.useMemo(() => {
    let localMinMax = {};

    const formattedData = queryData.liveStrokesGainedOverview.eventPlayers.map(
      (statsSummary) => {
        let newStatsSummary = { ...statsSummary };

        newStatsSummary.playerId = statsSummary.player.id;
        newStatsSummary.playerName =
          statsSummary.player.firstName + " " + statsSummary.player.lastName;

        if (statsSummary.player.dkPlayer) {
          newStatsSummary.dkPlayerId = statsSummary.player.dkPlayer.id;
        }

        [
          "strokesGainedApproachTheGreen",
          "strokesGainedApproachTheGreen",
          "strokesGainedAroundTheGreen",
          "strokesGainedBallStriking",
          "strokesGainedIronPlay",
          "strokesGainedOffTheTee",
          "strokesGainedPutting",
          "strokesGainedShortGame",
          "strokesGainedTeeToGreen",
          "strokesGainedTotal",
        ].forEach((stat) => {
          updateLocalMinMaxVal(
            localMinMax,
            stat,
            parseFloat(statsSummary[stat])
          );
        });

        return newStatsSummary;
      }
    );

    setMinMaxVals(localMinMax);

    return formattedData;
  }, [selectedEventID]);

  const columns = [
    {
      Header: "Pos",
      accessor: "currentPosition",
      sortDescFirst: false,
      sortType: sortGolfLeaderboardPositions,
    },
    {
      Header: "Name",
      accessor: "playerName",
      sortDescFirst: false,
    },
  ];

  if (nextRoundSalaryTitle) {
    columns.push({
      Header: nextRoundSalaryTitle,
      accessor: "nextRoundSalary",
      sortDescFirst: true,
    });
  }

  columns.push(
    ...[
      {
        Header: "To Par",
        accessor: "prettyEventToPar",
        sortType: sortGolfToPars,
        sortInverted: true,
      },
      {
        Header: "TOT",
        accessor: "strokesGainedTotal",
        sortInverted: false,
        sortType: sortFloats,
      },
      {
        Header: "T2G",
        accessor: "strokesGainedTeeToGreen",
        sortInverted: false,
        sortType: sortFloats,
      },
      {
        Header: "BALL",
        accessor: "strokesGainedBallStriking",
        sortInverted: false,
        sortType: sortFloats,
      },
      {
        Header: "SHORT",
        accessor: "strokesGainedShortGame",
        sortInverted: false,
        sortType: sortFloats,
      },
      {
        Header: "IRON",
        accessor: "strokesGainedIronPlay",
        sortInverted: false,
        sortType: sortFloats,
      },
      {
        Header: "OTT",
        accessor: "strokesGainedOffTheTee",
        sortInverted: false,
        sortType: sortFloats,
      },
      {
        Header: "APP",
        accessor: "strokesGainedApproachTheGreen",
        sortInverted: false,
        sortType: sortFloats,
      },
      {
        Header: "ARG",
        accessor: "strokesGainedAroundTheGreen",
        sortInverted: false,
        sortType: sortFloats,
      },
      {
        Header: "PUTT",
        accessor: "strokesGainedPutting",
        sortInverted: false,
        sortType: sortFloats,
      },
    ]
  );

  const myPlayerIds = myPlayers ? myPlayers.map((player) => player.value) : [];

  function showDKPlayerInfo(dkPlayerId) {
    if (dkPlayerId) {
      setSelectedDkPlayerId(dkPlayerId);
    }
  }

  function hideDKPlayerInfo() {
    setSelectedDkPlayerId(undefined);
  }

  return (
    <div className="pga-scorecards-container">
      <DraftkingsInfoOverlay
        show={selectedDkPlayerId !== undefined}
        playerId={selectedDkPlayerId}
        close={hideDKPlayerInfo}
      />
      <LiveTournamentToolTable
        data={data}
        columns={columns}
        roundNumber={roundNumber}
        myPlayerIds={myPlayerIds}
        showDrawer={false}
        minMaxVals={minMaxVals}
        showDKPlayerInfo={showDKPlayerInfo}
      />
    </div>
  );
}
